import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import { Feature } from '../analytics';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { getLocale } from '../getters/user';
import * as Pet from '../pet';
import { getChatbotMessageChannel } from '../chatbot/chatbot.getters';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';

const schema = {
  reengagement: SB.object({
    // We use loose schema validation for greater backwards compatibility but
    // cast as a tuple schema for strong compile-time checking against current ruleset
    day: SB.string() as unknown as SB.TupleSchema<'7' | '14' | '28'>,
  }),
};

export default {
  schema,
  actions: extractScheduledActionHandlers(
    createScheduledActions(schema)({
      reengagement: (state, { day }, api) => {
        const channel = getChatbotMessageChannel(
          state,
          { name: 'reengagement' },
          api.date.now(),
        );
        if (!channel) return;

        const {
          getEquippedPetSpecies,
          getAnalyticsUserProperties,
          getAnalyticsAttributeUserProperties,
          getPetName,
        } = Pet.getters;

        const payload = generateReplicantChatbotPayload({
          feature: Feature.NOTIFICATION._,
          $subFeature: getReengagementSubfeature(day),
        });

        // If no evolution skip
        if (!getEquippedPetSpecies(state)) return;

        const now = api.date.now();
        const message = getChatbotFlexMessage({
          template: 'reengagement',
          payload,
          data: {
            day,
            locale: getLocale(state),
            species: getEquippedPetSpecies(state)!,
            petName: getPetName(state),
          },
          analyticsUserProperties: {
            ...getAnalyticsUserProperties(state, now),
            ...getAnalyticsAttributeUserProperties(state, now),
          },
        });

        deliverChatbotMessage(state, api, {
          receiverId: state.id,
          message,
          channel,
        });
      },
    }),
  ),
};

function getReengagementSubfeature(day: '7' | '14' | '28') {
  switch (day) {
    case '7':
      return Feature.NOTIFICATION.D7_REENGAGEMENT;
    case '14':
      return Feature.NOTIFICATION.D14_REENGAGEMENT;
    case '28':
      return Feature.NOTIFICATION.D28_REENGAGEMENT;
    default:
      return getDefaultReengagementSubfeature(day);
  }
}

// Provides exhaustiveness check with a default value instead of
// throwing an error so we can maintain backwards compatibility
function getDefaultReengagementSubfeature(day: never) {
  return `${Feature.NOTIFICATION._}_${day}d_reengagement`;
}
